import api from '@/apis'
import { mockDelay } from '@/apis/mockDelay'
import { IS_MOCK, ORDER_API_BASE_URL } from '@/constants'
import { ApiError } from '@/types/api'
import type { ItemCategory, OrderAvailability } from '@/types/order'
import { ORDER_ERROR_CODES } from '@/types/order'

import { computeMockAvailability } from './mocks'

export interface GetOrderAvailabilityParams {
  /** 상품 대분류 */
  itemCategory: ItemCategory
  /** 지번 주소 (예: "경북 경산시 옥곡동 832-1"). 다음 우편번호 onComplete 응답의 jibunAddress */
  addressJibun: string
  /** 실시간 수거 여부 */
  isQuick: boolean
}

/**
 * 주문신청 가능여부 확인 — 주소 검증용으로 활용.
 *
 * Host: `ORDER_API_BASE_URL` (예: dev-onl-api.uglee.io/order/api/v2). service-area 와는 별도 백엔드라
 * 기본 axios baseURL 대신 endpoint 단위로 override. base 에 `orderBasePath/apiVersion` 까지 포함
 * (가챠 base 패턴과 일관 — placeholder 부분은 환경변수, path 는 도메인 endpoint 만).
 *
 * Path: `/orders/availability` (apidog OAS `/orderBasePath/apiVersion/orders/availability` 매핑)
 *
 * 응답:
 *  - 200 + isAvailable=true → 수거 가능
 *  - 422 E422200 → 서비스 가능 지역 아님 (ApiError throw)
 *  - 422 E422201 → 미납내역 존재 (ApiError throw)
 *  - 409 E409200 → 이미 신청된 주문 (ApiError throw)
 *
 * 호출자(AddressSearchPage)는 try/catch로 ApiError를 받아 `e.code`로 분기 처리.
 *
 * 인증: Bearer (회원만 호출 가능) — 인터셉터가 tokenStore 에서 자동 주입
 */
export const apiGetOrderAvailability = async ({
  itemCategory,
  addressJibun,
  isQuick,
}: GetOrderAvailabilityParams): Promise<OrderAvailability> => {
  if (IS_MOCK) {
    await mockDelay()
    return computeMockAvailability(addressJibun)
  }
  return await api.get<OrderAvailability>('/orders/availability', {
    baseURL: ORDER_API_BASE_URL,
    params: { itemCategory, addressJibun, isQuick },
  })
}

/**
 * ApiError가 "서비스 가능 지역 아님"인지 판별.
 * AddressSearchPage에서 수거 불가능 결과 화면 분기에 사용.
 */
export const isNotAvailableAreaError = (error: unknown): boolean =>
  error instanceof ApiError && error.code === ORDER_ERROR_CODES.NOT_AVAILABLE_AREA
